/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import * as StateManager from '../../store/state/state-manager';
import Utils from '../../utils/utils';
import { getNormalizedPropertyName } from '../../utils/form-handler';
import { listAction } from '../../store/state/actions/action-creators';
import type { Action } from '../../store/state/state-manager';
import type { C11nEnv } from '../../interpreter/c11n-env';

import Insert from './states/insert';
import Update from './states/update';
import Delete from './states/delete';
import DeleteAll from './states/delete-all';
import Move from './states/move';
import ReplacePage from './states/replace-page';
import DeletePage from './states/delete-page';
import StateNode from './state-node';
import type { StateNodeInstruction } from './state-node';

class StateMachine {
  public contextName: string;
  public target: string;
  public pageReference: string;
  public changeList: any;
  public targetPageReference: string;
  current: any;
  root: any;
  visibility: boolean;
  children: any[];
  constructor(target: string, pageReference: string, contextName: string) {
    this.target = target;
    this.pageReference = pageReference;
    this.contextName = contextName;
    this.root = null;
    this.current = null;
    this.changeList = {};
    this.targetPageReference = pageReference + target;
    this.children = []; // state machines of children
    this.visibility = true;
  }

  addChild(stateMachine: StateMachine) {
    if (this.current === null) {
      this.insertNode({});
    }
    this.current.childrenStateMachines.push(stateMachine);
  }

  insertNode(instruction: StateNodeInstruction) {
    const node = new StateNode(instruction);
    if (this.root === null) {
      this.root = node;
    }
    if (this.current !== null) {
      node.previous = this.current;
      this.current.next = node;
    }
    this.current = node;
  }

  buildChildSumbitPageInstructions(stateNode: { childrenStateMachines: any[] }, isOnlyChanged: boolean) {
    let instructions: any[] = [];
    stateNode.childrenStateMachines.forEach(
      (childStateMachine: { visibility: any; buildSubmitPageInstructions: (arg0: any) => any }) => {
        if (childStateMachine.visibility) {
          instructions = instructions.concat(childStateMachine.buildSubmitPageInstructions(isOnlyChanged));
        }
      }
    );
    return instructions;
  }

  // for form submit
  buildSubmitPageInstructions(isOnlyChanged = false) {
    let stateNode = this.root;
    let instructions: any[] = [];
    while (stateNode) {
      if (stateNode.instruction.instruction && !(isOnlyChanged && stateNode.automatic)) {
        instructions.push(stateNode.instruction);
      }
      instructions = [...instructions, ...this.buildChildSumbitPageInstructions(stateNode, isOnlyChanged)];
      stateNode = stateNode.next;
    }
    return instructions;
  }

  // push default page instruction
  initPageInstructions(c11nEnv: { getValue: (arg0: string) => any[] }, propertyNames: any[], uniqueField: string) {
    const rows = c11nEnv.getValue(this.target) || [];
    if (Array.isArray(rows)) {
      if (propertyNames) {
        rows.forEach((row: { [x: string]: any }, index: number) => {
          propertyNames.forEach((property: string) => {
            const normalizedProperty = getNormalizedPropertyName(property);
            const uuidPayload: { [key: string]: any } = {};
            let skipStateUpdate = true;
            if (uniqueField) {
              uuidPayload[getNormalizedPropertyName(uniqueField)] = crypto.randomUUID();
              skipStateUpdate = false;
            }
            const payload = {
              [normalizedProperty]: Utils.getIn(row, normalizedProperty),
              ...uuidPayload
            };
            this.update(payload, index + 1, skipStateUpdate, { isDefault: true, skipDirtyValidation: true });
          });
        });
        return;
      }
      rows.forEach((row: { [x: string]: any }, index: number) => {
        if (uniqueField && !row[getNormalizedPropertyName(uniqueField)]) {
          const payload: Action['payload'] = {};
          payload[getNormalizedPropertyName(uniqueField)] = crypto.randomUUID();
          this.update(payload, index + 1, false, {
            isDefault: true,
            skipDirtyValidation: true
          });
        }
      });
    }

    if (propertyNames && rows && typeof rows === 'object' && !Array.isArray(rows) && Object.keys(rows).length > 0) {
      this.processObjectRows(rows, propertyNames, uniqueField);
    }
  }

  processObjectRows(rows: any, propertyNames: any[], uniqueField: string) {
    Object.keys(rows).forEach((indexKey) => {
      const row = rows[indexKey];
      propertyNames.forEach((property) => {
        const normalizedProperty = getNormalizedPropertyName(property);
        const uuidPayload: { [key: string]: any } = {};
        let skipStateUpdate = true;
        if (uniqueField) {
          uuidPayload[getNormalizedPropertyName(uniqueField)] = crypto.randomUUID();
          skipStateUpdate = false;
        }
        const payload = {
          [`${normalizedProperty}`]: Utils.getIn(row, normalizedProperty),
          ...uuidPayload
        };
        this.update(payload, indexKey, skipStateUpdate, { isDefault: true });
      });
    });
  }

  // for refersh compare
  getChangeList() {
    return this.changeList;
  }

  initChangeList(index: string | number) {
    this.changeList[this.targetPageReference] = this.changeList[this.targetPageReference] || [];
    this.changeList[this.targetPageReference][index] = this.changeList[this.targetPageReference][index] || {};
  }

  // create page instructions node
  update(
    payload: object,
    index: string | number,
    skipStateUpdate: boolean,
    options: {
      skipPageinstruction?: boolean;
      isDefault?: boolean;
      isArrayDeepMerge?: boolean;
      skipDirtyValidation?: boolean;
    }
  ) {
    const { isDefault = false, skipPageinstruction, skipDirtyValidation = false } = options;
    const instruction = Update.execute(
      this.contextName,
      this.target,
      index,
      payload,
      this.pageReference,
      skipStateUpdate,
      skipDirtyValidation,
      isDefault
    );

    if (skipPageinstruction) {
      return;
    }
    if (typeof index === 'string') {
      this.initChangeList(index);
    } else {
      this.initChangeList(index - 1);
    }
    if (!isDefault) {
      const previousChange =
        this.changeList?.[this.targetPageReference]?.[typeof index === 'string' ? index : index - 1].content;
      this.changeList[this.targetPageReference][typeof index === 'string' ? index : index - 1] = StateManager.mergeDeep(
        Utils.deepClone(previousChange),
        payload,
        options
      );
    }
    if (
      this.current?.automatic === isDefault &&
      (this.current?.instruction?.groupIndex === index || this.current?.instruction?.listIndex === index) &&
      this.current?.instruction.instruction === 'UPDATE'
    ) {
      const previousContent = this.current.instruction.content;
      this.current.instruction.content = StateManager.mergeDeep(
        Utils.deepClone(previousContent),
        Utils.unflatten(payload),
        options
      );
      return;
    }
    this.insertNode(instruction);
    if (isDefault) {
      this.current.automatic = true;
    }
  }

  delete(index: number, skipStateUpdate: boolean | undefined) {
    const instruction = Delete.execute(this.target, index, this.pageReference);
    this.changeList[this.targetPageReference]?.splice(index - 1, 1);
    this.insertNode(instruction);
    if (!skipStateUpdate) {
      StateManager.dispatch(
        listAction({
          instruction: 'DELETE',
          listIndex: typeof index === 'string' ? index : index - 1,
          context: this.contextName,
          referenceList: `${this.pageReference}${this.target}`
        })
      );
    }
  }

  insert(
    payload: any,
    index: number | string,
    skipStateUpdate: boolean | undefined,
    options: { isDefault?: boolean; isArrayDeepMerge?: boolean; c11nEnv: C11nEnv }
  ) {
    /* istanbul ignore next */
    const {
      instruction,
      actionPayload = { content: {} },
      displayPropertPayload = {}
    } = Insert.execute(this.target, index, payload, this.pageReference);
    const { isDefault = false } = options;
    this.initChangeList(typeof index === 'string' ? index : index - 1);
    if (!isDefault) {
      const previousChange =
        this.changeList?.[this.targetPageReference]?.[typeof index === 'string' ? index : index - 1].content;
      this.changeList[this.targetPageReference][typeof index === 'string' ? index : index - 1] = StateManager.mergeDeep(
        Utils.deepClone(previousChange),
        payload,
        options
      );
    }
    if (this.root && !this.root.instruction?.instruction && this.current && !this.current.instruction?.instruction) {
      this.root.instruction = instruction;
    } else {
      this.insertNode(instruction);
    }
    if (!skipStateUpdate) {
      actionPayload.content = { ...actionPayload.content, ...displayPropertPayload };
      StateManager.dispatch(
        listAction({
          ...actionPayload,
          [typeof index === 'string' ? 'groupIndex' : 'listIndex']: typeof index === 'string' ? index : index - 1,
          c11nEnv: options.c11nEnv,
          context: this.contextName,
          referenceList: `${this.pageReference}${this.target}`
        })
      );
    }
    if (isDefault) {
      this.current.automatic = true;
    }
  }

  replacePage(
    payload: Record<string, string> & { nonFormProperties?: string[] },
    skipStateUpdate: boolean,
    target?: string,
    pageReference?: string
  ) {
    const effectivePageRef = pageReference ?? this.pageReference;
    const effectiveTarget = target ?? this.target;
    const { instruction, actionPayload } = ReplacePage.execute(effectiveTarget, payload, effectivePageRef);

    this.insertNode(instruction);
    if (!skipStateUpdate) {
      StateManager.dispatch(
        listAction({
          ...actionPayload,
          context: this.contextName,
          referenceList: `${effectivePageRef}${Utils.convertClipboardPageToPageRefNotation(effectiveTarget)}`
        })
      );
    }
  }

  deletePage(skipStateUpdate: boolean) {
    const instruction = DeletePage.execute(this.target, this.pageReference);
    this.insertNode(instruction);

    if (!skipStateUpdate) {
      StateManager.dispatch(
        listAction({
          instruction: 'DELETEPAGE',
          context: this.contextName,
          referenceList: `${this.pageReference}${this.target}`
        })
      );
    }
  }

  move(fromIndex: number, toIndex: number) {
    const value = this.changeList[this.targetPageReference]?.splice(fromIndex - 1, 1);
    this.changeList[this.targetPageReference]?.splice(toIndex - 1, 0, value[0]);
    const instruction = Move.execute(this.target, fromIndex, toIndex, this.pageReference);
    this.insertNode(instruction);
    StateManager.dispatch(
      listAction({
        listIndex: fromIndex - 1,
        listMoveToIndex: toIndex - 1,
        context: this.contextName,
        referenceList: `${this.pageReference}${this.target}`,
        instruction: 'MOVE'
      })
    );
  }

  deleteAll(skipStateUpdate: boolean) {
    const instruction = DeleteAll.getDeleteAllInstruction(this.pageReference, this.target);
    this.changeList[this.targetPageReference] = [];
    this.insertNode(instruction);
    DeleteAll.execute(this.contextName, this.target, this.pageReference, skipStateUpdate);
  }

  updatePage(payload: any) {
    const previousContent = this.current.instruction.content;
    this.current.instruction.content = StateManager.mergeDeep(Utils.deepClone(previousContent), payload);
  }
}

export default StateMachine;
